import React from 'react';
import { 
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import NoDataIcon from '@mui/icons-material/AssignmentLate';

// 字符串转颜色函数
const stringToColor = (string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

// 生成头像属性函数
const stringAvatar = (firstName, lastName) => {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  return {
    sx: {
      bgcolor: stringToColor(firstName + lastName),
      width: 48,
      height: 48,
      border: '2px solid #f5f5f5',
      fontSize: '16px'
    },
    children: initials
  };
};

const getStatusColor = (status) => {
  const statusColors = {
    "Reward Pending": "#4CAF50",
    "In Progress": "#2196F3",
    "Link Expiring Soon": "#FF9800",
    "Short Payment": "#FF9800",
    "Waiting for Signup": "#9E9E9E",
    "Reward Issued": "#4CAF50"
  };
  return statusColors[status] || "#000";
};

// 无数据展示组件
const EmptyState = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 3,
      textAlign: 'center'
    }}
  >
    <NoDataIcon 
      sx={{ 
        fontSize: { xs: 48, sm: 64 },
        color: '#A3A3A3',
        mb: 2,
        opacity: 0.8
      }} 
    />
    <Typography
      variant="h6"
      sx={{
        color: '#1a1a1a',
        fontWeight: 600,
        mb: 1,
        fontSize: { xs: '1rem', sm: '1.25rem' }
      }}
    >
      No Referrals Yet
    </Typography>
    <Typography
      variant="body1"
      sx={{
        color: '#666',
        maxWidth: '300px',
        lineHeight: 1.5,
        fontSize: { xs: '0.875rem', sm: '1rem' }
      }}
    >
      Share your referral link with friends to start earning rewards
    </Typography>
  </Box>
);

const ReferralsList = ({ referralData = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmall = useMediaQuery('(max-width:360px)');
  
  // 检查是否有数据
  if (!referralData || referralData.length === 0) {
    return (
      <Box sx={{ 
        bgcolor: '#fff',
        borderRadius: '8px',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <EmptyState />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: '#fff',
      borderRadius: '8px',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      <List sx={{ 
        p: 0,
        '& .MuiListItem-root': {
          px: { xs: 1.5, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          transition: 'background-color 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.02)'
          }
        }
      }}>
        {referralData.map((referral, index) => {
          const fullName = `${referral.firstName || ''} ${referral.lastName || ''}`.trim();
          
          return (
            <React.Fragment key={referral.id || index}>
              <ListItem
                sx={{
                  alignItems: 'center',
                  gap: { xs: 1, sm: 2 },
                  flexWrap: isExtraSmall ? 'wrap' : 'nowrap'
                }}
              >
                <ListItemAvatar>
                  {referral.avatar ? (
                    <Avatar 
                      src={referral.avatar}
                      sx={{ 
                        width: { xs: 40, sm: 48 }, 
                        height: { xs: 40, sm: 48 },
                        border: '2px solid #f5f5f5',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
                      }} 
                    />
                  ) : (
                    <Avatar 
                      {...stringAvatar(referral.firstName, referral.lastName)}
                      sx={(theme) => ({
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        border: '2px solid #f5f5f5',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                        fontSize: { xs: '14px', sm: '16px' },
                        fontWeight: 500,
                        letterSpacing: '0.5px'
                      })}
                    />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '14px', sm: '16px' },
                        color: '#1a1a1a',
                        mb: 0.5,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {fullName}
                    </Typography>
                  }
                  secondary={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        fontSize: { xs: '12px', sm: '14px' },
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {referral.email}
                    </Typography>
                  }
                  sx={{ 
                    my: 0,
                    maxWidth: isExtraSmall ? '100%' : 'calc(100% - 140px)'
                  }}
                />
                <Box
                  sx={{
                    bgcolor: getStatusColor(referral.status),
                    color: '#fff',
                    fontWeight: 500,
                    borderRadius: '24px',
                    px: { xs: 0.75, sm: 1 },
                    py: { xs: 0.5, sm: 1 },
                    fontSize: { xs: '12px', sm: '14px' },
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.3px',
                    display: 'flex',
                    alignItems: 'center',
                    height: { xs: '28px', sm: '32px' },
                    minWidth: { xs: '90px', sm: '100px' },
                    justifyContent: 'center',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.15)'
                    },
                    ml: isExtraSmall ? 'auto' : 0,
                    mt: isExtraSmall ? 1 : 0,
                    order: isExtraSmall ? 3 : 'initial'
                  }}
                >
                  {referral.status}
                </Box>
              </ListItem>
              {index < referralData.length - 1 && (
                <Divider 
                  sx={{ 
                    mx: { xs: 1.5, sm: 3 },
                    borderColor: 'rgba(0, 0, 0, 0.08)'
                  }} 
                />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default ReferralsList;
